import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { Label } from '@components/atoms/Label';
import {
  PaintCalculatorInputs,
  PaintCalculatorResults,
  OnAddToMaterialCallback,
} from '../../types/calculator.types';
import { Plus } from 'lucide-react';
import { useTranslation } from '@hooks/useTranslation';

interface PaintCalculatorProps {
  onAddToMaterial?: OnAddToMaterialCallback;
}

const PaintCalculator: React.FC<PaintCalculatorProps> = ({
  onAddToMaterial,
}) => {
  const tCalc = useTranslation('calculators');
  const [inputs, setInputs] = useState<PaintCalculatorInputs>({
    roomLength: '',
    roomWidth: '',
    roomHeight: '',
    numDoors: '',
    numWindows: '',
    coveragePerGallon: 400,
    numCoats: 2,
  });

  const [results, setResults] = useState<PaintCalculatorResults | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Scroll to results when they appear
  useEffect(() => {
    if (results && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [results]);

  const handleInputChange =
    (field: keyof PaintCalculatorInputs) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setInputs((prev) => ({
        ...prev,
        [field]:
          field === 'coveragePerGallon' || field === 'numCoats'
            ? Number(value)
            : value,
      }));
    };

  const calculatePaint = () => {
    setResults(null);

    const parsedRoomLength = parseFloat(inputs.roomLength);
    const parsedRoomWidth = parseFloat(inputs.roomWidth);
    const parsedRoomHeight = parseFloat(inputs.roomHeight);
    const parsedNumDoors = parseInt(inputs.numDoors) || 0;
    const parsedNumWindows = parseInt(inputs.numWindows) || 0;
    const parsedCoverage = parseFloat(inputs.coveragePerGallon.toString());
    const parsedCoats = parseInt(inputs.numCoats.toString());

    if (
      isNaN(parsedRoomLength) ||
      isNaN(parsedRoomWidth) ||
      isNaN(parsedRoomHeight) ||
      parsedRoomLength <= 0 ||
      parsedRoomWidth <= 0 ||
      parsedRoomHeight <= 0
    ) {
      return;
    }
    if (isNaN(parsedCoverage) || parsedCoverage <= 0) {
      return;
    }
    if (isNaN(parsedCoats) || parsedCoats <= 0) {
      return;
    }

    try {
      const perimeter = 2 * (parsedRoomLength + parsedRoomWidth);
      const wallArea = perimeter * parsedRoomHeight;

      const doorArea = parsedNumDoors * 21;
      const windowArea = parsedNumWindows * 12;

      const paintableArea = wallArea - doorArea - windowArea;

      if (paintableArea < 0) {
        return;
      }

      const paintNeededInGallons = (paintableArea / parsedCoverage) * parsedCoats;
      const finalGallons = Math.ceil(paintNeededInGallons);

      setResults({
        totalSurfaceArea: paintableArea.toFixed(2),
        paintNeeded: finalGallons,
      });
    } catch (error) {
      console.error('Calculation failed:', error);
    }
  };

  return (
    <div>
      <h3 className="text-xl font-semibold text-center text-gray-900 dark:text-white mb-6">
        {tCalc.names.paint}
      </h3>

      {/* Room Dimensions */}
      <div className="mb-6">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {tCalc.dimensions.roomDimensionsFt}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label label={tCalc.dimensions.length} htmlFor="roomLength" />
            <Input
              id="roomLength"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 20`}
              value={inputs.roomLength}
              onChange={handleInputChange('roomLength')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.dimensions.width} htmlFor="roomWidth" />
            <Input
              id="roomWidth"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 15`}
              value={inputs.roomWidth}
              onChange={handleInputChange('roomWidth')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.dimensions.height} htmlFor="roomHeight" />
            <Input
              id="roomHeight"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 8`}
              value={inputs.roomHeight}
              onChange={handleInputChange('roomHeight')}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Doors & Windows */}
      <div className="mb-6">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {tCalc.paint.doorsAndWindowsCount}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label label={tCalc.counts.doors} htmlFor="numDoors" />
            <Input
              id="numDoors"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 1`}
              value={inputs.numDoors}
              onChange={handleInputChange('numDoors')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.counts.windows} htmlFor="numWindows" />
            <Input
              id="numWindows"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 2`}
              value={inputs.numWindows}
              onChange={handleInputChange('numWindows')}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Paint Information */}
      <div className="mb-6">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {tCalc.paint.paintInformation}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label
              label={tCalc.paint.coverageSqFtGallon}
              htmlFor="coveragePerGallon"
            />
            <Input
              id="coveragePerGallon"
              type="number"
              value={inputs.coveragePerGallon}
              onChange={handleInputChange('coveragePerGallon')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.counts.coats} htmlFor="numCoats" />
            <Input
              id="numCoats"
              type="number"
              value={inputs.numCoats}
              onChange={handleInputChange('numCoats')}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      <Button
        fullWidth
        variant="primary"
        size="lg"
        onClick={calculatePaint}
        className="mb-6">
        {tCalc.paint.calculateButton}
      </Button>

      {results && (
        <div
          ref={resultsRef}
          className="p-4 mb-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border-t-4 border-indigo-600">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
            {tCalc.actions.results}
          </h4>
          <div className="space-y-3">
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-semibold">{tCalc.paint.totalPaintableArea}</span>{' '}
              <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                {results.totalSurfaceArea}
              </span>{' '}
              {tCalc.dimensions.sqFt}
            </p>
            <div className="flex items-center justify-between">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold">{tCalc.paint.gallonsNeeded}</span>{' '}
                <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                  {results.paintNeeded}
                </span>{' '}
                {results.paintNeeded === 1
                  ? tCalc.paint.unitGallonOne
                  : tCalc.paint.unitGallonOther}
              </p>
              {onAddToMaterial && (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() =>
                    onAddToMaterial({
                      amount: results.paintNeeded,
                      amountType: 'gallons',
                      amountSystem: 'imperial',
                      suggestedName: tCalc.materials.paint,
                      resultLabel: tCalc.paint.gallonsNeeded,
                    })
                  }>
                  {tCalc.actions.addToMaterial}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaintCalculator;
